import { useMemo, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import {
  Plus,
  Pencil,
  Trash2,
  Loader2,
  ChevronDown,
  KeyRound,
  Copy,
  Search,
  Shield,
  Briefcase,
  Wrench,
  UserPlus,
} from 'lucide-react';
import { apiService } from '@/core/api/service';
import { useAppState } from '@/core/app-context';
import { PageHeader } from '@/components/PageHeader';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { toast } from 'sonner';
import type { Tenant, TenantOperator, BackofficeRole } from '@/core/models';

export const Route = createFileRoute('/backoffice-users')({
  head: () => ({
    meta: [{ title: 'Utenze Backoffice · Replya Internal Backoffice' }],
  }),
  component: BackofficeUsersPage,
});

function getRandomChar(chars: string): string {
  const array = new Uint32Array(1);
  crypto.getRandomValues(array);
  return chars[array[0] % chars.length];
}

function generatePassword(length = 12): string {
  const uppercase = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
  const lowercase = 'abcdefghijklmnopqrstuvwxyz';
  const digits = '0123456789';
  const special = '@$!%*?&_#^~+=-';
  const all = uppercase + lowercase + digits + special;

  const password = [
    getRandomChar(uppercase),
    getRandomChar(lowercase),
    getRandomChar(digits),
    getRandomChar(special),
  ];

  for (let i = password.length; i < length; i++) {
    password.push(getRandomChar(all));
  }

  for (let i = password.length - 1; i > 0; i--) {
    const array = new Uint32Array(1);
    crypto.getRandomValues(array);
    const j = array[0] % (i + 1);
    [password[i], password[j]] = [password[j], password[i]];
  }

  return password.join('');
}

function RoleBadge({
  role,
  canCreateUsers,
}: {
  role: BackofficeRole;
  canCreateUsers?: boolean;
}) {
  if (role === 'admin') {
    return (
      <Badge className="bg-purple-600/20 text-purple-300 border-purple-500/30 gap-1 font-medium">
        <Shield className="h-3 w-3" />
        Admin
      </Badge>
    );
  }
  if (role === 'business') {
    return (
      <div className="flex flex-wrap items-center gap-1.5">
        <Badge className="bg-blue-600/20 text-blue-300 border-blue-500/30 gap-1 font-medium">
          <Briefcase className="h-3 w-3" />
          Business
        </Badge>
        {canCreateUsers && (
          <Badge
            variant="outline"
            className="text-[10px] bg-blue-500/10 text-blue-300 border-blue-500/30 gap-1 py-0 px-1.5 h-5 font-normal"
          >
            <UserPlus className="h-2.5 w-2.5" />
            Crea utenti
          </Badge>
        )}
      </div>
    );
  }
  return (
    <div className="flex flex-wrap items-center gap-1.5">
      <Badge className="bg-amber-600/20 text-amber-300 border-amber-500/30 gap-1 font-medium">
        <Wrench className="h-3 w-3" />
        Tecnico
      </Badge>
      {canCreateUsers && (
        <Badge
          variant="outline"
          className="text-[10px] bg-blue-500/10 text-blue-300 border-blue-500/30 gap-1 py-0 px-1.5 h-5 font-normal"
        >
          <UserPlus className="h-2.5 w-2.5" />
          Crea utenti
        </Badge>
      )}
    </div>
  );
}

function TenantSelect({
  tenants,
  value,
  onChange,
}: {
  tenants: Tenant[];
  value: string;
  onChange: (value: string) => void;
}) {
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="outline"
          className="w-full justify-between rounded-xl bg-surface-2 border-border"
        >
          <span className="truncate">
            {value
              ? tenants.find((t) => t.tenantId === value)?.name ?? value
              : 'Seleziona tenant'}
          </span>
          <ChevronDown className="h-4 w-4 opacity-50" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent className="w-64 max-h-64 overflow-y-auto">
        {tenants.length === 0 ? (
          <div className="px-2 py-1.5 text-sm text-muted-foreground">
            Nessun tenant disponibile
          </div>
        ) : (
          tenants.map((t) => (
            <DropdownMenuItem key={t.tenantId} onClick={() => onChange(t.tenantId)}>
              {t.name}
            </DropdownMenuItem>
          ))
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

interface CreateOperatorDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  tenants: Tenant[];
  defaultTenantId?: string;
  onSubmit: (data: {
    tenantId: string;
    username: string;
    displayName: string;
    role: BackofficeRole;
    canCreateUsers: boolean;
    password: string;
    active: boolean;
  }) => void;
  isPending: boolean;
}

function CreateOperatorDialog({
  open,
  onOpenChange,
  tenants,
  defaultTenantId,
  onSubmit,
  isPending,
}: CreateOperatorDialogProps) {
  const [tenantId, setTenantId] = useState(defaultTenantId ?? '');
  const [username, setUsername] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [role, setRole] = useState<BackofficeRole>('tecnico');
  const [canCreateUsers, setCanCreateUsers] = useState(false);
  const [password, setPassword] = useState(generatePassword);
  const [active, setActive] = useState(true);

  const canSubmit = tenantId.trim() && username.trim() && password.length >= 8;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!canSubmit) return;
    onSubmit({
      tenantId: tenantId.trim(),
      username: username.trim(),
      displayName: displayName.trim() || username.trim(),
      role,
      canCreateUsers: role === 'admin' ? true : canCreateUsers,
      password,
      active,
    });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-2xl sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Nuova utenza backoffice tenant</DialogTitle>
          <DialogDescription>
            Crea un account operatore con livello di accesso dedicato per il portale del tenant.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label>Tenant</Label>
            <TenantSelect tenants={tenants} value={tenantId} onChange={setTenantId} />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-2">
              <Label htmlFor="create-username">Username</Label>
              <Input
                id="create-username"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                placeholder="es. marco.rossi"
                className="rounded-xl bg-surface-2 border-border"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="create-display-name">Nome visualizzato</Label>
              <Input
                id="create-display-name"
                value={displayName}
                onChange={(e) => setDisplayName(e.target.value)}
                placeholder="es. Marco Rossi"
                className="rounded-xl bg-surface-2 border-border"
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label>Livello di accesso (Ruolo)</Label>
            <div className="grid grid-cols-3 gap-2">
              <button
                type="button"
                onClick={() => setRole('tecnico')}
                className={`p-3 rounded-xl border text-left transition-all ${
                  role === 'tecnico'
                    ? 'border-amber-500/80 bg-amber-500/10'
                    : 'border-border bg-surface-2 hover:bg-surface-3'
                }`}
              >
                <div className="flex items-center gap-1.5 font-medium text-xs mb-1">
                  <Wrench className="h-3.5 w-3.5 text-amber-400" />
                  Tecnico
                </div>
                <p className="text-[11px] text-muted-foreground leading-tight">
                  Catalogo, correzioni e documenti
                </p>
              </button>

              <button
                type="button"
                onClick={() => setRole('business')}
                className={`p-3 rounded-xl border text-left transition-all ${
                  role === 'business'
                    ? 'border-blue-500/80 bg-blue-500/10'
                    : 'border-border bg-surface-2 hover:bg-surface-3'
                }`}
              >
                <div className="flex items-center gap-1.5 font-medium text-xs mb-1">
                  <Briefcase className="h-3.5 w-3.5 text-blue-400" />
                  Business
                </div>
                <p className="text-[11px] text-muted-foreground leading-tight">
                  Analytics e consultazione sola lettura
                </p>
              </button>

              <button
                type="button"
                onClick={() => setRole('admin')}
                className={`p-3 rounded-xl border text-left transition-all ${
                  role === 'admin'
                    ? 'border-purple-500/80 bg-purple-500/10'
                    : 'border-border bg-surface-2 hover:bg-surface-3'
                }`}
              >
                <div className="flex items-center gap-1.5 font-medium text-xs mb-1">
                  <Shield className="h-3.5 w-3.5 text-purple-400" />
                  Admin
                </div>
                <p className="text-[11px] text-muted-foreground leading-tight">
                  Accesso totale, config e storico
                </p>
              </button>
            </div>
          </div>

          <div className="flex items-center justify-between p-3 rounded-xl bg-surface-2 border border-border">
            <div>
              <Label htmlFor="create-can-create-users" className="font-medium text-sm">
                Permesso creazione utenti
              </Label>
              <p className="text-xs text-muted-foreground">
                Permette a questo utente di creare e gestire altre utenze per il proprio tenant.
              </p>
            </div>
            <Switch
              id="create-can-create-users"
              checked={role === 'admin' || canCreateUsers}
              disabled={role === 'admin'}
              onCheckedChange={setCanCreateUsers}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="create-password">Password iniziale</Label>
            <div className="flex gap-2">
              <Input
                id="create-password"
                type="text"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="rounded-xl bg-surface-2 border-border font-mono"
              />
              <Button
                type="button"
                variant="outline"
                onClick={() => setPassword(generatePassword())}
                className="rounded-xl shrink-0"
              >
                Rigenera
              </Button>
            </div>
            <p className="text-xs text-muted-foreground">
              Minimo 8 caratteri. La password viene salvata come hash bcrypt.
            </p>
          </div>

          <div className="flex items-center justify-between p-3 rounded-xl bg-surface-2 border border-border">
            <div>
              <Label htmlFor="create-active" className="font-medium text-sm">Utenza attiva</Label>
              <p className="text-xs text-muted-foreground">
                Se disattivata, l'accesso al portale sarà negato immediatamente.
              </p>
            </div>
            <Switch id="create-active" checked={active} onCheckedChange={setActive} />
          </div>

          <DialogFooter>
            <Button
              type="submit"
              disabled={isPending || !canSubmit}
              className="rounded-xl bg-brand hover:bg-brand-glow text-brand-foreground"
            >
              {isPending ? 'Salvataggio...' : 'Crea utente'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

interface EditOperatorDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  operator: TenantOperator | null;
  onSubmit: (data: {
    displayName: string;
    role: BackofficeRole;
    canCreateUsers: boolean;
    active: boolean;
  }) => void;
  isPending: boolean;
}

function EditOperatorDialog({
  open,
  onOpenChange,
  operator,
  onSubmit,
  isPending,
}: EditOperatorDialogProps) {
  const [displayName, setDisplayName] = useState('');
  const [role, setRole] = useState<BackofficeRole>('tecnico');
  const [canCreateUsers, setCanCreateUsers] = useState(false);
  const [active, setActive] = useState(true);

  // Aggiorna lo stato locale quando cambia l'operatore selezionato
  const operatorId = operator?.id ?? operator?.username ?? '';
  useMemo(() => {
    if (operator) {
      setDisplayName(operator.displayName || operator.username);
      setRole(operator.role);
      setCanCreateUsers(operator.canCreateUsers === true);
      setActive(operator.active !== false);
    }
  }, [operatorId]);

  if (!operator) return null;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSubmit({
      displayName: displayName.trim() || operator.username,
      role,
      canCreateUsers: role === 'admin' ? true : canCreateUsers,
      active,
    });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-2xl sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Modifica utente: {operator.username}</DialogTitle>
          <DialogDescription>
            Tenant: <span className="font-medium text-foreground">{operator.tenantName}</span>
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="edit-display-name">Nome visualizzato</Label>
            <Input
              id="edit-display-name"
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              className="rounded-xl bg-surface-2 border-border"
            />
          </div>

          <div className="space-y-2">
            <Label>Ruolo</Label>
            <div className="grid grid-cols-3 gap-2">
              <button
                type="button"
                onClick={() => setRole('tecnico')}
                className={`p-2.5 rounded-xl border text-center transition-all ${
                  role === 'tecnico'
                    ? 'border-amber-500/80 bg-amber-500/10 font-medium'
                    : 'border-border bg-surface-2 hover:bg-surface-3'
                }`}
              >
                Tecnico
              </button>
              <button
                type="button"
                onClick={() => setRole('business')}
                className={`p-2.5 rounded-xl border text-center transition-all ${
                  role === 'business'
                    ? 'border-blue-500/80 bg-blue-500/10 font-medium'
                    : 'border-border bg-surface-2 hover:bg-surface-3'
                }`}
              >
                Business
              </button>
              <button
                type="button"
                onClick={() => setRole('admin')}
                className={`p-2.5 rounded-xl border text-center transition-all ${
                  role === 'admin'
                    ? 'border-purple-500/80 bg-purple-500/10 font-medium'
                    : 'border-border bg-surface-2 hover:bg-surface-3'
                }`}
              >
                Admin
              </button>
            </div>
          </div>

          <div className="flex items-center justify-between p-3 rounded-xl bg-surface-2 border border-border">
            <div>
              <Label htmlFor="edit-can-create-users" className="font-medium text-sm">
                Permesso creazione utenti
              </Label>
              <p className="text-xs text-muted-foreground">
                Permette a questo utente di creare e gestire altre utenze per il proprio tenant.
              </p>
            </div>
            <Switch
              id="edit-can-create-users"
              checked={role === 'admin' || canCreateUsers}
              disabled={role === 'admin'}
              onCheckedChange={setCanCreateUsers}
            />
          </div>

          <div className="flex items-center justify-between p-3 rounded-xl bg-surface-2 border border-border">
            <div>
              <Label htmlFor="edit-active" className="font-medium text-sm">Stato utente</Label>
              <p className="text-xs text-muted-foreground">
                {active ? 'Account abilitato al login' : 'Account disattivato'}
              </p>
            </div>
            <Switch id="edit-active" checked={active} onCheckedChange={setActive} />
          </div>

          <DialogFooter>
            <Button
              type="submit"
              disabled={isPending}
              className="rounded-xl bg-brand hover:bg-brand-glow text-brand-foreground"
            >
              {isPending ? 'Salvataggio...' : 'Salva modifiche'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

interface ResetPasswordDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  operator: TenantOperator | null;
  onSubmit: (password: string) => void;
  isPending: boolean;
}

function ResetPasswordDialog({
  open,
  onOpenChange,
  operator,
  onSubmit,
  isPending,
}: ResetPasswordDialogProps) {
  const [password, setPassword] = useState(generatePassword);

  if (!operator) return null;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (password.length < 8) return;
    onSubmit(password);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-2xl sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Reimposta password</DialogTitle>
          <DialogDescription>
            Reimposta la password per l'utente{' '}
            <span className="font-medium text-foreground">{operator.username}</span> ({operator.tenantName}).
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="reset-pwd">Nuova password</Label>
            <div className="flex gap-2">
              <Input
                id="reset-pwd"
                type="text"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="rounded-xl bg-surface-2 border-border font-mono"
              />
              <Button
                type="button"
                variant="outline"
                onClick={() => setPassword(generatePassword())}
                className="rounded-xl shrink-0"
              >
                Rigenera
              </Button>
            </div>
          </div>
          <DialogFooter>
            <Button
              type="submit"
              disabled={isPending || password.length < 8}
              className="rounded-xl bg-brand hover:bg-brand-glow text-brand-foreground"
            >
              {isPending ? 'Salvataggio...' : 'Conferma nuova password'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function BackofficeUsersPage() {
  const queryClient = useQueryClient();
  const { selectedTenant } = useAppState();
  const [createOpen, setCreateOpen] = useState(false);
  const [editingOperator, setEditingOperator] = useState<TenantOperator | null>(null);
  const [resetPwdOperator, setResetPwdOperator] = useState<TenantOperator | null>(null);
  const [deleteOperator, setDeleteOperator] = useState<TenantOperator | null>(null);
  const [filterByTenant, setFilterByTenant] = useState(false);
  const [roleFilter, setRoleFilter] = useState<'all' | BackofficeRole>('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [createdCredentials, setCreatedCredentials] = useState<{
    tenantName: string;
    username: string;
    password: string;
  } | null>(null);

  const { data: tenants } = useQuery({
    queryKey: ['tenants'],
    queryFn: () => apiService.listTenants(),
  });

  const { data: operators, isLoading } = useQuery({
    queryKey: ['tenant-operators'],
    queryFn: () => apiService.listTenantOperators(),
  });

  const createMutation = useMutation({
    mutationFn: ({
      tenantId,
      body,
    }: {
      tenantId: string;
      body: Parameters<typeof apiService.upsertTenantOperator>[1];
    }) => apiService.upsertTenantOperator(tenantId, body),
    onSuccess: (_, vars) => {
      toast.success('Utenza backoffice creata con successo');
      setCreateOpen(false);
      setCreatedCredentials({
        tenantName:
          tenants?.find((t) => t.tenantId === vars.tenantId)?.name ?? vars.tenantId,
        username: vars.body.username,
        password: vars.body.password || '',
      });
      queryClient.invalidateQueries({ queryKey: ['tenant-operators'] });
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Errore durante la creazione');
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({
      tenantId,
      userId,
      body,
    }: {
      tenantId: string;
      userId: string;
      body: {
        displayName: string;
        role: BackofficeRole;
        canCreateUsers: boolean;
        active: boolean;
      };
    }) => {
      if (userId.startsWith('legacy-')) {
        return apiService.upsertTenantOperator(tenantId, {
          username: editingOperator?.username || '',
          displayName: body.displayName,
          role: body.role,
          canCreateUsers: body.canCreateUsers,
          active: body.active,
        });
      }
      return apiService.updateTenantOperator(tenantId, userId, body);
    },
    onSuccess: () => {
      toast.success('Utenza aggiornata con successo');
      setEditingOperator(null);
      queryClient.invalidateQueries({ queryKey: ['tenant-operators'] });
    },
    onError: (error: Error) => {
      toast.error(error.message || "Errore durante l'aggiornamento");
    },
  });

  const resetPasswordMutation = useMutation({
    mutationFn: async ({
      tenantId,
      userId,
      password,
    }: {
      tenantId: string;
      userId: string;
      password: string;
    }) => {
      if (userId.startsWith('legacy-')) {
        await apiService.upsertTenantOperator(tenantId, {
          username: resetPwdOperator?.username || '',
          password,
        });
        return;
      }
      await apiService.resetTenantOperatorPassword(tenantId, userId, password);
    },
    onSuccess: (_, vars) => {
      toast.success('Password reimpostata con successo');
      setResetPwdOperator(null);
      setCreatedCredentials({
        tenantName:
          tenants?.find((t) => t.tenantId === vars.tenantId)?.name ?? vars.tenantId,
        username: resetPwdOperator?.username || '',
        password: vars.password,
      });
      queryClient.invalidateQueries({ queryKey: ['tenant-operators'] });
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Errore durante il reset password');
    },
  });

  const deleteMutation = useMutation({
    mutationFn: ({ tenantId, userId }: { tenantId: string; userId?: string }) =>
      apiService.deleteTenantOperator(tenantId, userId?.startsWith('legacy-') ? undefined : userId),
    onSuccess: () => {
      toast.success('Utenza rimossa con successo');
      setDeleteOperator(null);
      queryClient.invalidateQueries({ queryKey: ['tenant-operators'] });
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Errore durante la rimozione');
    },
  });

  const filteredOperators = useMemo(() => {
    let list = operators ?? [];
    if (filterByTenant && selectedTenant) {
      list = list.filter((o) => o.tenantId === selectedTenant.tenantId);
    }
    if (roleFilter !== 'all') {
      list = list.filter((o) => o.role === roleFilter);
    }
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase().trim();
      list = list.filter(
        (o) =>
          o.username.toLowerCase().includes(q) ||
          (o.displayName && o.displayName.toLowerCase().includes(q)) ||
          o.tenantName.toLowerCase().includes(q)
      );
    }
    return list;
  }, [operators, selectedTenant, filterByTenant, roleFilter, searchQuery]);

  return (
    <div className="space-y-6 animate-fadeIn">
      <PageHeader
        title="Utenze Backoffice"
        subtitle="Gestione centralizzata madre degli operatori, ruoli e credenziali per tutti i tenant Replya."
      >
        <Button
          onClick={() => setCreateOpen(true)}
          className="rounded-xl bg-brand hover:bg-brand-glow text-brand-foreground"
        >
          <Plus className="mr-2 h-4 w-4" />
          Nuovo utente
        </Button>
      </PageHeader>

      <CreateOperatorDialog
        open={createOpen}
        onOpenChange={setCreateOpen}
        tenants={tenants ?? []}
        defaultTenantId={selectedTenant?.tenantId}
        onSubmit={(data) => createMutation.mutate({ tenantId: data.tenantId, body: data })}
        isPending={createMutation.isPending}
      />

      <EditOperatorDialog
        open={editingOperator !== null}
        onOpenChange={(open) => !open && setEditingOperator(null)}
        operator={editingOperator}
        onSubmit={(data) => {
          if (!editingOperator) return;
          updateMutation.mutate({
            tenantId: editingOperator.tenantId,
            userId: editingOperator.id || editingOperator.username,
            body: data,
          });
        }}
        isPending={updateMutation.isPending}
      />

      <ResetPasswordDialog
        open={resetPwdOperator !== null}
        onOpenChange={(open) => !open && setResetPwdOperator(null)}
        operator={resetPwdOperator}
        onSubmit={(password) => {
          if (!resetPwdOperator) return;
          resetPasswordMutation.mutate({
            tenantId: resetPwdOperator.tenantId,
            userId: resetPwdOperator.id || resetPwdOperator.username,
            password,
          });
        }}
        isPending={resetPasswordMutation.isPending}
      />

      <AlertDialog
        open={deleteOperator !== null}
        onOpenChange={(open) => !open && setDeleteOperator(null)}
      >
        <AlertDialogContent className="rounded-2xl">
          <AlertDialogHeader>
            <AlertDialogTitle>Rimuovi utenza</AlertDialogTitle>
            <AlertDialogDescription>
              Sei sicuro di voler rimuovere l'utenza{' '}
              <span className="font-medium text-foreground">
                {deleteOperator?.displayName || deleteOperator?.username}
              </span>{' '}
              per il tenant <Badge variant="secondary">{deleteOperator?.tenantName}</Badge>?
              <br />
              L'azione cancellerà l'account e ne impedirà l'accesso immediato.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-xl">Annulla</AlertDialogCancel>
            <AlertDialogAction
              onClick={() =>
                deleteOperator &&
                deleteMutation.mutate({
                  tenantId: deleteOperator.tenantId,
                  userId: deleteOperator.id,
                })
              }
              className="rounded-xl bg-destructive hover:bg-destructive/90 text-destructive-foreground"
            >
              {deleteMutation.isPending ? 'Rimozione...' : 'Rimuovi'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <Dialog
        open={createdCredentials !== null}
        onOpenChange={() => setCreatedCredentials(null)}
      >
        <DialogContent className="rounded-2xl sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Credenziali salvate</DialogTitle>
            <DialogDescription>
              L'utenza è stata salvata con successo. Condividi le credenziali con l'operatore.
            </DialogDescription>
          </DialogHeader>
          {createdCredentials && (
            <div className="space-y-4 py-2">
              <div className="space-y-2">
                <Label htmlFor="op-tenant">Tenant</Label>
                <Input
                  id="op-tenant"
                  value={createdCredentials.tenantName}
                  readOnly
                  className="rounded-xl bg-surface-2 border-border"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="op-username">Username</Label>
                <div className="flex gap-2">
                  <Input
                    id="op-username"
                    value={createdCredentials.username}
                    readOnly
                    className="rounded-xl bg-surface-2 border-border font-medium"
                  />
                  <Button
                    type="button"
                    variant="outline"
                    size="icon"
                    className="rounded-xl shrink-0"
                    onClick={() => {
                      navigator.clipboard.writeText(createdCredentials.username);
                      toast.success('Username copiato');
                    }}
                    title="Copia username"
                  >
                    <Copy className="h-4 w-4" />
                  </Button>
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="op-password">Password</Label>
                <div className="flex gap-2">
                  <Input
                    id="op-password"
                    type="text"
                    value={createdCredentials.password}
                    readOnly
                    className="rounded-xl bg-surface-2 border-border font-mono font-medium"
                  />
                  <Button
                    type="button"
                    variant="outline"
                    size="icon"
                    className="rounded-xl shrink-0"
                    onClick={() => {
                      navigator.clipboard.writeText(createdCredentials.password);
                      toast.success('Password copiata');
                    }}
                    title="Copia password"
                  >
                    <Copy className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              className="rounded-xl"
              onClick={() => setCreatedCredentials(null)}
            >
              Chiudi
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Barra filtri */}
      <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-3 rounded-2xl border bg-surface p-3">
        <div className="flex flex-1 items-center gap-2">
          <div className="relative flex-1 max-w-sm">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Cerca username, nome o tenant..."
              className="pl-9 rounded-xl bg-surface-2 border-border h-9"
            />
          </div>

          <div className="flex items-center gap-1 border-l border-border pl-2">
            <Button
              variant={roleFilter === 'all' ? 'default' : 'ghost'}
              size="sm"
              onClick={() => setRoleFilter('all')}
              className="rounded-lg text-xs h-8 px-2.5"
            >
              Tutti ({operators?.length ?? 0})
            </Button>
            <Button
              variant={roleFilter === 'admin' ? 'default' : 'ghost'}
              size="sm"
              onClick={() => setRoleFilter('admin')}
              className="rounded-lg text-xs h-8 px-2.5"
            >
              Admin
            </Button>
            <Button
              variant={roleFilter === 'business' ? 'default' : 'ghost'}
              size="sm"
              onClick={() => setRoleFilter('business')}
              className="rounded-lg text-xs h-8 px-2.5"
            >
              Business
            </Button>
            <Button
              variant={roleFilter === 'tecnico' ? 'default' : 'ghost'}
              size="sm"
              onClick={() => setRoleFilter('tecnico')}
              className="rounded-lg text-xs h-8 px-2.5"
            >
              Tecnico
            </Button>
          </div>
        </div>

        {selectedTenant && (
          <div className="flex items-center gap-2 border-t sm:border-t-0 sm:border-l border-border pt-2 sm:pt-0 sm:pl-3">
            <Label htmlFor="filter-by-tenant" className="text-xs text-muted-foreground cursor-pointer">
              Solo <span className="font-medium text-foreground">{selectedTenant.name}</span>
            </Label>
            <Switch
              id="filter-by-tenant"
              checked={filterByTenant}
              onCheckedChange={setFilterByTenant}
            />
          </div>
        )}
      </div>

      {/* Tabella utenti */}
      <div className="rounded-2xl border bg-surface overflow-hidden">
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Tenant</TableHead>
                <TableHead>Utente</TableHead>
                <TableHead>Ruolo</TableHead>
                <TableHead>Stato</TableHead>
                <TableHead>Password</TableHead>
                <TableHead className="w-36 text-right">Azioni</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {isLoading ? (
                <TableRow>
                  <TableCell colSpan={6} className="text-center text-muted-foreground py-8">
                    <Loader2 className="inline h-5 w-5 animate-spin mr-2" />
                    Caricamento utenti backoffice...
                  </TableCell>
                </TableRow>
              ) : filteredOperators.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} className="text-center text-muted-foreground py-8">
                    Nessuna utenza trovata con i filtri attuali.
                  </TableCell>
                </TableRow>
              ) : (
                filteredOperators.map((operator) => {
                  const itemKey =
                    operator.id || `${operator.tenantId}-${operator.username}`;
                  return (
                    <TableRow key={itemKey}>
                      <TableCell className="font-medium">
                        <Badge variant="outline" className="text-xs font-semibold">
                          {operator.tenantName}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <div className="flex flex-col">
                          <span className="font-semibold text-foreground text-sm">
                            {operator.username}
                          </span>
                          {operator.displayName && operator.displayName !== operator.username && (
                            <span className="text-xs text-muted-foreground">
                              {operator.displayName}
                            </span>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>
                        <RoleBadge
                          role={operator.role}
                          canCreateUsers={operator.canCreateUsers}
                        />
                      </TableCell>
                      <TableCell>
                        {operator.active === false || operator.disabled ? (
                          <Badge variant="destructive" className="text-xs">
                            Disattivato
                          </Badge>
                        ) : (
                          <Badge
                            variant="default"
                            className="text-xs bg-emerald-600/20 text-emerald-300 border-emerald-500/30"
                          >
                            Attivo
                          </Badge>
                        )}
                      </TableCell>
                      <TableCell>
                        {operator.hasPassword ? (
                          <Badge
                            variant="outline"
                            className="text-xs text-emerald-500 border-emerald-500/30"
                          >
                            <KeyRound className="mr-1 h-3 w-3" />
                            Configurata
                          </Badge>
                        ) : (
                          <Badge variant="outline" className="text-xs text-muted-foreground">
                            Non configurata
                          </Badge>
                        )}
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex items-center justify-end gap-1">
                          <Button
                            variant="ghost"
                            size="icon"
                            className="rounded-xl h-8 w-8"
                            onClick={() => setResetPwdOperator(operator)}
                            title="Reimposta password"
                          >
                            <KeyRound className="h-4 w-4 text-muted-foreground hover:text-foreground" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="rounded-xl h-8 w-8"
                            onClick={() => setEditingOperator(operator)}
                            title="Modifica ruolo e stato"
                          >
                            <Pencil className="h-4 w-4 text-muted-foreground hover:text-foreground" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="rounded-xl h-8 w-8"
                            onClick={() => setDeleteOperator(operator)}
                            title="Elimina"
                          >
                            <Trash2 className="h-4 w-4 text-destructive" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </div>
      </div>
    </div>
  );
}
